import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import Modal from './Modal';
import { useAsync } from './ui';

// design/030 — owners remove members from a Group / Event / Course / Practice,
// with a refund choice when the person paid on the web (Stripe).

const errText = (e) => (e?.data?.message || e?.message || 'Something went wrong').replace(/^[A-Z_]+:\s*/, '');

export function money(cents, currency = 'usd') {
  const c = (currency || 'usd').toLowerCase();
  return `${c === 'usd' ? '$' : `${c.toUpperCase()} `}${(cents / 100).toFixed(2)}`;
}

/** Payments for one item: { priced, payments: { personId: {...} } } (owners only). */
export function useMemberPayments(kind, itemId) {
  return useAsync(
    () => api.memberPayments(kind, itemId).catch(() => ({ priced: false, payments: {} })),
    [kind, itemId]);
}

/** "Paid $10.00" / "Refunded $10.00" pill for a member row. */
export function PaidPill({ payment }) {
  if (!payment) return null;
  const fullyRefunded = payment.refundedCents > 0 && payment.refundableCents === 0;
  return (
    <span className="pill" style={{
      marginLeft: 6,
      background: fullyRefunded ? '#f3f4f6' : '#ecfdf5',
      color: fullyRefunded ? 'var(--muted)' : 'var(--green-text, #15803d)',
    }}>
      {fullyRefunded ? `Refunded ${money(payment.refundedCents, payment.currency)}`
        : `Paid ${money(payment.amountCents, payment.currency)}`}
    </span>
  );
}

/**
 * Remove button + dialog. `payment` is this member's entry from
 * useMemberPayments (or undefined), `priced` whether the item costs money.
 */
export function RemoveMemberButton({ kind, itemId, itemName, personId, name, payment, priced, onRemoved }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="btn btn-sm" style={{ color: '#b91c1c', borderColor: '#fca5a5' }}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}>Remove</button>
      {open && (
        <RemoveMemberModal kind={kind} itemId={itemId} itemName={itemName} personId={personId} name={name}
          payment={payment} priced={priced}
          onClose={() => setOpen(false)}
          onDone={() => { setOpen(false); onRemoved?.(); }} />
      )}
    </>
  );
}

function RemoveMemberModal({ kind, itemId, itemName, personId, name, payment, priced, onClose, onDone }) {
  const refundable = payment && payment.refundable && payment.refundableCents > 0;
  const [mode, setMode] = useState(refundable ? 'FULL' : 'NONE');   // decision: full refund by default
  const [amount, setAmount] = useState(refundable ? (payment.refundableCents / 100).toFixed(2) : '');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const noun = { group: 'group', event: 'event', course: 'course', practice: 'practice' }[kind] || kind;

  const submit = async () => {
    let amountCents;
    if (mode === 'PARTIAL') {
      amountCents = Math.round(parseFloat(amount) * 100);
      if (!amountCents || amountCents <= 0 || amountCents > payment.refundableCents) {
        alert(`Enter an amount between $0.01 and ${money(payment.refundableCents, payment.currency)}.`);
        return;
      }
    }
    setBusy(true);
    try {
      const r = await api.removeMember(kind, itemId, personId, { refund: mode, amountCents, note: note.trim() || undefined });
      if (r?.refundedCents > 0) {
        alert(`${name} was removed and refunded ${money(r.refundedCents, payment?.currency)}.`);
      }
      onDone();
    } catch (e) {
      alert(errText(e));
      setBusy(false);
    }
  };

  const label = mode === 'NONE' ? 'Remove'
    : mode === 'FULL' ? `Remove and refund ${money(payment.refundableCents, payment.currency)}`
      : 'Remove and refund';

  return (
    <Modal title={`Remove ${name}?`} onClose={onClose}
      footer={<>
        <button className="btn" disabled={busy} onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" disabled={busy} onClick={submit}
          style={{ background: '#b91c1c', borderColor: '#b91c1c' }}>{busy ? 'Removing…' : label}</button>
      </>}>
      <p style={{ marginTop: 0, marginBottom: 16, fontSize: '0.9rem', lineHeight: 1.5 }}>
        {name} will be removed from the {noun} <b>{itemName}</b> and will lose access right away.
        {kind === 'group' || kind === 'event' ? ' They also leave its conversation.' : ' Their progress is kept in case they rejoin.'}
        {' '}They'll get an email letting them know.
      </p>

      {payment ? (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: 12, marginBottom: 12 }}>
          <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 6 }}>
            ⚠️ {name} paid {money(payment.amountCents, payment.currency)}
            {payment.paidAt ? ` on ${new Date(payment.paidAt).toLocaleDateString()}` : ''} for this {noun}.
            {payment.refundedCents > 0 ? ` ${money(payment.refundedCents, payment.currency)} has already been refunded.` : ''}
          </div>
          {refundable ? (
            <div style={{ display: 'grid', gap: 6 }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="radio" checked={mode === 'FULL'} onChange={() => setMode('FULL')} />
                Refund in full — {money(payment.refundableCents, payment.currency)}
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="radio" checked={mode === 'PARTIAL'} onChange={() => setMode('PARTIAL')} />
                Refund part:
                <input className="input" style={{ width: 110 }} type="number" min="0.01" step="0.01"
                  max={(payment.refundableCents / 100).toFixed(2)} value={amount}
                  onFocus={() => setMode('PARTIAL')} onChange={(e) => setAmount(e.target.value)} />
              </label>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="radio" checked={mode === 'NONE'} onChange={() => setMode('NONE')} />
                No refund
              </label>
              <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>
                Refunds go back to their card through Stripe and usually appear within 5–10 days.
              </div>
            </div>
          ) : (
            <div style={{ fontSize: '0.875rem' }}>Nothing left to refund.</div>
          )}
        </div>
      ) : priced ? (
        <div style={{ background: '#f9fafb', border: '1px solid var(--border)', borderRadius: 10, padding: 12, marginBottom: 12, fontSize: '0.875rem' }}>
          This {noun} has a price. If {name} paid before refunds were tracked in the app (or joined by invite),
          there's no payment to refund here — refund it in the{' '}
          <a href="https://dashboard.stripe.com/payments" target="_blank" rel="noreferrer">Stripe dashboard</a> if needed.
        </div>
      ) : null}

      <div className="form-field">
        <label>Message to {name} (optional)</label>
        <textarea className="textarea" value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="Included in the email they receive" maxLength={500} />
      </div>
    </Modal>
  );
}

/** Link to one program / offering's member list in Manage your members. */
export function membersUrl(tab, id) {
  return `/admin/learn-members?tab=${tab}${id ? `&id=${id}` : ''}`;
}

/**
 * Decided 2026-10-02 — a program / offering with members can't be deleted;
 * the owner removes them first in Manage members (refunds happen there).
 * `count` = members other than the owner. Returns true when it handled it.
 */
export function blockDeleteWithMembers(count, noun, tab, id, navigate) {
  if (!count) return false;
  const people = count === 1 ? '1 person is' : `${count} people are`;
  if (window.confirm(`${people} in this ${noun}, so it can't be deleted yet.\n\n`
    + 'Remove them first in Manage members (you can refund paid members there), then delete it.\n\n'
    + 'Open Manage members now?')) {
    navigate(membersUrl(tab, id));
  }
  return true;
}

/** Same message when the server refuses (409 HAS_MEMBERS). Returns true if handled. */
export function handleHasMembersError(e, tab, id, navigate) {
  const msg = e?.data?.message || e?.message || '';
  if (e?.status !== 409 || !/HAS_MEMBERS/.test(msg)) return false;
  if (window.confirm(`${msg.replace(/^HAS_MEMBERS:\s*/, '')}\n\nOpen Manage members now?`)) {
    if (navigate) navigate(membersUrl(tab, id));
    else window.location.assign(membersUrl(tab, id));
  }
  return true;
}

/**
 * Members count for the Manage pages (owner not counted). Click → that
 * program's member list in Manage members. `count` undefined = still loading.
 */
export function MembersCountCell({ count, tab, id }) {
  const navigate = useNavigate();
  if (count === undefined) return <span style={{ color: 'var(--muted)' }}>…</span>;
  return (
    <a onClick={(e) => { e.stopPropagation(); navigate(membersUrl(tab, id)); }}
      title="Open in Manage members" style={{ cursor: 'pointer', fontWeight: 600 }}>
      {count}
    </a>
  );
}
